import {Inject, Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {share, tap} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {AuthStreamingService} from './auth-streaming.service';
import {GenericStreamingService, WebSocketCommand, WebSocketMessage} from './streaming-interfaces';
import {SubscriptionCommand, SubscriptionDescriptor} from './subscription-descriptor';
import {SubscriptionSubjectType} from './subscription-subject-type';

export type SubscriptionSubject = string[];

interface SubscriptionsMap {
    [subjectTypeName: string]: SubscriptionDescriptor;
}

@Injectable()
export class SubscriptionStreamingService {

    protected subscriptionsMap: SubscriptionsMap = {};
    protected resubscribing$: Observable<any>;
    protected message$: Observable<WebSocketMessage>;
    protected actualize$ = new Subject<SubscriptionDescriptor>();
    protected command$ = new Subject<SubscriptionCommand>();

    constructor(
        @Inject(AuthStreamingService)
        protected streamingService: GenericStreamingService
    ) {
        this.resubscribing$ = this.createSubscriptionKeeperStream();
        this.message$ = this.keepConnectionWhileSubscription(this.streamingService.message$);

        this.command$.subscribe(cmd => this.sendOperation(cmd));
    }

    public createSubscription<T>(
        subjectType: SubscriptionSubjectType<T>,
        subject?: SubscriptionSubject
    ): Observable<T> {
        const descriptor: SubscriptionDescriptor = this.getSubscriptionDescriptor(subjectType);

        return descriptor.createSubscription(subject);
    }

    public resendSubscription(subjectType: SubscriptionSubjectType, subject: SubscriptionSubject): void {
        const descriptor: SubscriptionDescriptor = this.subscriptionsMap[subjectType.name];

        if (descriptor) {
            descriptor.resendSubscription(subjectType, subject);
        }
    }

    protected sendOperation(cmd: SubscriptionCommand): void {
        const command: WebSocketCommand = {
            operation: cmd.operation,
            requestId: cmd.requestId,
            data:      cmd.subject
        };

        this.streamingService.sendCommand(command);
    }

    protected getSubscriptionDescriptor(subjectType: SubscriptionSubjectType): SubscriptionDescriptor {
        return this.subscriptionsMap[subjectType.name] = this.subscriptionsMap[subjectType.name]
            || new SubscriptionDescriptor(
                subjectType,
                this.streamingService.ready$,
                this.streamingService.notReady$,
                this.actualize$,
                this.command$,
                this.message$
            );
    }

    protected keepConnectionWhileSubscription<T>(observable: Observable<T>): Observable<T> {
        return observable.pipe(
            bindWith(this.resubscribing$)
        );
    }

    protected createSubscriptionKeeperStream(): Observable<any> {
        return this.streamingService.notReady$.pipe(
            tap(() => this.actualize$.next(null)), // Arrange actualization on the next connection.
            share()
        );
    }

}
